<template>
  <div class="report-view flex flex-col">
    <template v-if="!adAccountId">
      <page-header v-if="$root.isLoggedIn" :title="tr('Report')">
        <template v-if="!isSharedReportView && !$app.isShoprenterApp" #buttons>
          <ui-button :disabled="isLoadingShare" @click="() => share()">
            <loader-circle-icon v-if="isLoadingShare" class="animate-spin" />
            <share-2-icon v-else />
            {{ tr('Share') }}
          </ui-button>
        </template>
      </page-header>
      <a
        v-else
        href="https://infinite.ad"
        target="_blank"
        class="mx-auto mt-8 no-underline"
      >
        <app-brand-mark />
      </a>
    </template>

    <div class="flex flex-col gap-5 py-5">
      <div v-if="isSharedReportView && isFetchingToken" class="flex justify-center py-20">
        <loader-circle-icon class="size-8 animate-spin text-primary" />
      </div>
      <empty-screen
        v-else-if="isSharedReportView && isInvalidReportToken"
        icon="stat"
        :title="tr('This report link is no longer available or might be incorrect')"
        :flag-text="tr('Invalid report link')"
        :description="tr('The report you\'re trying to access might have expired or been removed. Please check if you\'ve entered the URL correctly, or contact the person who shared it with you to get an updated link.')"
      />
      <empty-screen
        v-else-if="!isSharedReportView && !hasConnectedAdAccount"
        icon="stat"
        :title="tr('Connect your ad account to see analytics')"
        :flag-text="tr('Real-time insights await')"
        :description="tr('Track your campaign performance, monitor spending, and analyze key metrics all in one place. To start seeing your data, connect your Meta ad account and let our AI help you optimize your advertising results.')"
        path="/integrations/meta"
      />
      <template v-else>
        <info-text
          type="info"
          collapsible
          closable
          dismiss-key="report-attribution-notice"
          :dismiss-ttl-hours="24"
        >
          <template #title>
            {{ tr('The numbers shown here may differ from those in Meta Ads Manager.') }}
          </template>
          {{ tr('An ad in Ads Manager may show only click-based conversions if its attribution view there is set to click-only. :app reports, however, always use the Meta default attribution (7-day click + 1-day view), which also counts view-through conversions. Please take this into account when analyzing the data.') }}
        </info-text>

        <section class="glass-panel grid gap-4 rounded-xl p-5 sm:grid-cols-2 lg:grid-cols-6">
          <div v-if="adAccountId === null" class="flex min-w-0 flex-col gap-1.5 lg:col-span-2">
            <ui-label>{{ tr('Ad Account') }}</ui-label>
            <ui-combobox-select
              v-model="selectedAdAccountId"
              :options="adAccountList"
              option-value="id"
              option-label="name"
              :placeholder="tr('Select Ad account')"
              :search-placeholder="tr('Search') + '...'"
              :empty-text="tr('No results')"
              :disabled="isSharedReportView || disabledFilters"
            />
          </div>

          <div class="flex min-w-0 flex-col gap-1.5 lg:col-span-2">
            <ui-label>{{ tr('Ad status') }}</ui-label>
            <ui-select v-model="adStatusSelectValue" :disabled="disabledFilters">
              <ui-select-trigger class="w-full" :aria-label="tr('Ad status')">
                <ui-select-value>
                  <span v-if="selectedAdStatus" class="flex items-center gap-2">
                    <span v-if="selectedAdStatus.dot" :class="[ 'size-2 shrink-0 rounded-full', selectedAdStatus.dot ]" />
                    {{ tr(selectedAdStatus.label) }}
                  </span>
                </ui-select-value>
              </ui-select-trigger>
              <ui-select-content>
                <ui-select-item v-for="option in adStatusList" :key="String(option.value)" :value="String(option.value)">
                  <span v-if="option.dot" :class="[ 'size-2 rounded-full', option.dot ]" />
                  {{ tr(option.label) }}
                </ui-select-item>
              </ui-select-content>
            </ui-select>
          </div>

          <div class="flex min-w-0 flex-col gap-1.5 lg:col-span-2">
            <ui-label>{{ tr('Period') }}</ui-label>
            <ui-select
              :model-value="selectedPeriod ?? undefined"
              :disabled="periods.length === 0 || !selectedAdAccountId || !selectedAction || disabledFilters"
              @update:model-value="(value) => selectedPeriod = value"
            >
              <ui-select-trigger class="w-full" :aria-label="tr('Period')">
                <ui-select-value />
              </ui-select-trigger>
              <ui-select-content>
                <ui-select-item v-for="item in periods" :key="item.machine_name" :value="item.machine_name">
                  {{ ucfirst(tr(item.label)) }}
                </ui-select-item>
              </ui-select-content>
            </ui-select>
          </div>

          <div v-if="campaignId === null" class="flex min-w-0 flex-col gap-1.5 lg:col-span-3">
            <ui-label>{{ tr('Campaign') }}</ui-label>
            <ui-combobox-select
              v-model="selectedCampaignId"
              :options="campaigns"
              option-value="id"
              :option-label="(option) => option.id === null ? tr(option.name) : option.name"
              :search-placeholder="tr('Search') + '...'"
              :empty-text="tr('No results')"
              :disabled="!selectedAdAccountId || isSharedReportView || disabledFilters"
            >
              <template #option="{ option, label }">
                <span v-if="'status' in option" :class="[ 'size-2 shrink-0 rounded-full', option.status == 1 ? 'bg-success' : 'bg-destructive' ]" />
                <span class="truncate">{{ label }}</span>
              </template>
            </ui-combobox-select>
          </div>

          <div :class="[ 'flex min-w-0 flex-col gap-1.5', campaignId === null ? 'lg:col-span-3' : 'lg:col-span-2' ]">
            <ui-label>{{ tr('Report aspect') }}</ui-label>
            <ui-select
              :model-value="selectedAction ?? undefined"
              :disabled="!selectedAdAccountId || !selectedAction || disabledFilters"
              @update:model-value="(value) => selectedAction = value"
            >
              <ui-select-trigger class="w-full" :aria-label="tr('Report aspect')">
                <ui-select-value />
              </ui-select-trigger>
              <ui-select-content>
                <ui-select-item v-for="item in actions" :key="item.machine_name" :value="item.machine_name">
                  {{ item.label }}
                </ui-select-item>
              </ui-select-content>
            </ui-select>
          </div>
        </section>

        <div class="grid grid-cols-2 gap-5 lg:grid-cols-[repeat(auto-fit,minmax(11rem,1fr))]">
          <div v-for="tile in statTiles" :key="tile.key" class="glass-panel flex min-h-32 flex-col justify-between gap-3 rounded-xl p-5">
            <div class="flex items-start justify-between gap-2">
              <h2 class="m-0 text-sm font-semibold text-foreground">{{ tile.label }}</h2>
              <component :is="tile.icon" class="size-4.5 shrink-0 text-muted-foreground" />
            </div>
            <ui-skeleton v-if="tile.isLoading" class="h-8 w-24" />
            <div v-else class="flex flex-col gap-1">
              <span class="text-3xl leading-none font-bold tracking-tight text-foreground tabular-nums">
                {{ tile.value }}
                <small v-if="tile.unit" class="text-sm font-semibold text-muted-foreground">{{ tile.unit }}</small>
              </span>
              <span v-if="tile.hint" class="text-xs font-semibold text-muted-foreground">{{ tile.hint }}</span>
            </div>
          </div>
        </div>

        <report-ads-table
          ref="reportAdsTable"
          :ad-account-id="selectedAdAccountId"
          :campaign-id="selectedCampaignId"
          :action="selectedAction"
          :period="selectedPeriod"
          :currency="adAccountCurrency"
          :is-active-filter="isActiveFilter"
          :token-part1="tokenPart1"
          :token-part2="tokenPart2"
        />
      </template>
    </div>
  </div>
</template>

<script>
import {
  ChartLine,
  CircleDollarSign,
  Eye,
  LoaderCircle,
  MousePointerClick,
  Share2,
  Wallet,
} from '@lucide/vue';
import { markRaw } from 'vue';

import { NonCriticalError } from '@/errors';
import ReportService from '@/services/Report';
import AppBrandMark from '@/ui/components/AppBrandMark.vue';
import EmptyScreen from '@/ui/components/EmptyScreen.vue';
import InfoText from '@/ui/components/InfoText.vue';
import PageHeader from '@/ui/components/PageHeader.vue';
import ReportAdsTable from '@/ui/components/ReportAdsTable.vue';
import ReportShareModal from '@/ui/components/ReportShareModal.vue';
import { formatCompactNumber } from '@/utils/number.js';
import { ucfirst } from '@/utils/string.js';

export default {
  name: 'ReportView',
  components: {
    AppBrandMark,
    EmptyScreen,
    InfoText,
    LoaderCircleIcon: LoaderCircle,
    ReportAdsTable,
    Share2Icon: Share2,
    PageHeader,
  },
  props: {
    adAccountId: {
      type: [ String, null ],
      default: null,
    },
    campaignId: {
      type: [ String, null ],
      default: null,
    },
    action: {
      type: [ String, null ],
      default: null,
    },
    period: {
      type: [ String, null ],
      default: null,
    },
  },
  data() {
    return {
      hasError: false,
      isLoadingPeriods: false,
      isLoadingActions: false,
      isLoadingCampaigns: false,
      isLoadingTotals: false,
      isLoadingShare: false,
      isFetchingToken: false,
      isInvalidReportToken: false,

      sharedAdAccount: null,

      isActiveFilter: true,
      selectedAdAccountId: this.adAccountId,

      periods: [],
      selectedPeriod: this.period,

      actions: [],
      selectedAction: this.action,

      campaigns: [ this.buildAllCampaignsOption() ],
      selectedCampaignId: this.campaignId,
      ignoreCampaignWatcher: false,

      totals: null,

      shareUrl: null,
    };
  },
  computed: {
    hasConnectedAdAccount() {
      return this.$root.loggedInUser && this.$root.loggedInUser.company.ad_accounts.length !== 0;
    },
    isSharedReportView() {
      return this.$route.name === 'SharedReport';
    },
    tokenPart1() {
      return this.$route.params.tokenPart1;
    },
    tokenPart2() {
      return this.$route.params.tokenPart2;
    },
    disabledFilters() {
      return this.isLoadingActions
        || this.isLoadingTotals
        || this.isLoadingCampaigns
        || (this.$refs.reportAdsTable && this.$refs.reportAdsTable.isLoading);
    },
    // The kit select works with strings: "null" / "true" / "false".
    adStatusSelectValue: {
      get() {
        return String(this.isActiveFilter);
      },
      set(value) {
        this.isActiveFilter = value === 'null' ? null : value === 'true';
      },
    },
    statTiles() {
      const loading = this.isLoadingActions || (this.isLoadingTotals && !this.totals);
      const totals = this.totals || {};
      const money = value => value ? this.formatCompactNumber(value) : '–';
      const tiles = [
        { key: 'spend', label: this.tr('Spend'), icon: Wallet, value: money(totals.spend), unit: totals.spend ? this.adAccountCurrency : null, isLoading: loading },
      ];

      if (this.isPurchaseAction) {
        tiles.push({ key: 'revenue', label: this.tr('Revenue'), icon: CircleDollarSign, value: money(totals.revenue), unit: totals.revenue ? this.adAccountCurrency : null, isLoading: loading });
      }

      const strictLoading = this.isLoadingActions || this.isLoadingTotals;
      tiles.push(
        { key: 'impressions', label: this.tr('Impressions'), icon: Eye, value: money(totals.impressions), isLoading: loading },
        { key: 'actions', label: this.tr('Actions'), icon: MousePointerClick, value: money(totals.actions), hint: this.selectedActionLabel ? this.tr(this.selectedActionLabel) : null, isLoading: strictLoading },
        {
          key: 'cpa',
          label: this.tr('Average CPA'),
          icon: ChartLine,
          value: money(totals.avg_cpa),
          unit: totals.avg_cpa ? this.adAccountCurrency : null,
          hint: this.selectedActionLabel
            ? this.tr('Cost') + ' / ' + this.tr(this.selectedActionLabel) + (totals.roas !== null && totals.roas !== undefined ? ' (' + this.tr('ROAS') + ': ' + this.formatCompactNumber(totals.roas) + ')' : '')
            : null,
          isLoading: strictLoading,
        },
      );

      return tiles.map(tile => ({ ...tile, icon: markRaw(tile.icon) }));
    },
    selectedAdStatus() {
      return this.adStatusList.find(option => option.value === this.isActiveFilter) ?? null;
    },
    adStatusList() {
      return [
        {
          label: 'All',
          value: null,
        },
        {
          label: 'Active ads',
          value: true,
          dot: 'bg-success',
        },
        {
          label: 'Inactive ads',
          value: false,
          dot: 'bg-destructive',
        },
      ];
    },
    adAccountList() {
      const result = [];
      if (this.$root.isLoggedIn) {
        result.push(...this.$root.currentCompany.ad_accounts);
      }

      if (this.sharedAdAccount !== null) {
        if (!result.map(i => i.id).includes(this.sharedAdAccount.id)) {
          result.push(this.sharedAdAccount);
        }
      }

      return result;
    },
    selectedAdAccount() {
      return this.adAccountList.find(i => i.id === this.selectedAdAccountId);
    },
    adAccountCurrency() {
      const fallback = '$';
      if (!this.selectedAdAccount) {
        return fallback;
      }

      const currency = this.$store.getters['app/currencies'].find(i => i.code === this.selectedAdAccount.currency);

      return currency ? currency.symbol : fallback;
    },
    completedFilters() {
      return this.selectedAdAccountId && this.selectedAction && this.selectedPeriod;
    },
    selectedActionLabel() {
      if (!this.selectedAction) {
        return null;
      }

      const actionItem = this.actions.find(action => action.machine_name === this.selectedAction);

      return actionItem ? actionItem.label : null;
    },
    isPurchaseAction() {
      return this.selectedAction && this.selectedAction.includes('purchase');
    },
  },
  watch: {
    selectedAdAccountId(val) {
      if (!val) {
        return;
      }

      this.resetTotals();
      if (!this.isSharedReportView) {
        this.resetActions();
        this.resetCampaigns();
      }

      this.getActions()
        .then(resp => {
          if (!this.isSharedReportView) {
            this.getCampaigns();
          }

          return resp;
        })
        .catch(err => this.error(err));
    },
    selectedCampaignId(val, oldVal) {
      if (!this.completedFilters || this.ignoreCampaignWatcher) {
        return;
      }

      if (val !== null && val !== oldVal) {
        this.getDefaultAction()
          .then(() => {
            if (this.completedFilters) {
              this.resetTotals();
              return this.getTotals();
            }
            return null;
          })
          .catch(err => this.error(err));
      }
      else if (val === null) {
        this.getDefaultAction()
          .then(() => {
            this.resetTotals();
            if (this.completedFilters) {
              this.getTotals();
              return;
            }
            return;
          })
          .catch(err => this.error(err));
      }
    },

    selectedAction() {
      if (!this.completedFilters) {
        return;
      }

      this.getTotals();
    },
    selectedPeriod() {
      if (!this.completedFilters) {
        return;
      }

      this.resetTotals();
      this.getTotals();
    },
  },
  created() {
    if (this.isSharedReportView) {
      this.fetchReportByToken();
    }

    this.getPeriods();
    if (this.adAccountId !== null) {
      this.getActions();
    }
    if (this.campaignId !== null) {
      this.getDefaultAction();
    }

    const urlParams = new URLSearchParams(window.location.search);
    const selectedAdAccountId = urlParams.get('ad_account');
    const campaignId = urlParams.get('campaign');
    const action = urlParams.get('action');
    const period = urlParams.get('period');

    if (campaignId || selectedAdAccountId) {
      this.$router.replace(window.location.pathname);
    }

    if (selectedAdAccountId) {
      this.selectedAdAccountId = selectedAdAccountId;
    }

    this.$nextTick(() => {
      if (campaignId) {
        this.selectedAction = action;
        this.selectedCampaignId = campaignId;
      }
      if (period) {
        this.selectedPeriod = period;
      }
    });
  },
  methods: {
    ucfirst,
    buildAllCampaignsOption() {
      return {
        id: null,
        name: 'All campaigns',
      };
    },
    formatCompactNumber,
    resetTotals() {
      this.totals = null;

      this.isLoadingTotals = false;
    },
    resetActions() {
      this.actions = [];
      this.selectedAction = null;

      this.isLoadingActions = false;
    },
    resetCampaigns() {
      this.campaigns = [];
      this.selectedCampaignId = null;

      this.isLoadingCampaigns = false;
    },
    resetView() {
      this.hasError = false;
    },
    getPeriods() {
      if (this.isLoadingPeriods) {
        return Promise.reject(new NonCriticalError(this.tr('Please wait until the previous operation is completed.')));
      }

      this.isLoadingPeriods = true;

      return ReportService.getPeriods()
        .then(resp => {
          this.periods = resp.periods;
          if (this.selectedPeriod === null) {
            this.selectedPeriod = resp.default_period;
          }

          return resp;
        })
        .catch(err => this.onError(err))
        .finally(() => this.isLoadingPeriods = false);
    },
    getActions() {
      if (this.isLoadingActions) {
        return Promise.reject(new NonCriticalError(this.tr('Please wait until the previous operation is completed.')));
      }

      this.isLoadingActions = true;

      return ReportService.getActions({
        tokenPart1: this.tokenPart1,
        tokenPart2: this.tokenPart2,
        adAccountId: this.selectedAdAccountId,
      })
        .then(resp => {
          this.actions = resp.actions;
          if (resp.default_action === null) {
            this.selectedAction = null;
            return resp;
          }

          const availableActionValues = this.actions.map(i => i.machine_name);
          if (
            this.selectedAction !== null
            && !availableActionValues.includes(this.selectedAction)
            && this.selectedAction.startsWith('omni_')
          ) {
            this.selectedAction = this.selectedAction.replace('omni_', '');
          }

          if (
            !this.isSharedReportView
            && (this.selectedAction === null || !this.actions.map(i => i.machine_name).includes(this.selectedAction))
          ) {
            this.selectedAction = resp.default_action;
          }

          return resp;
        })
        .catch(err => this.onError(err))
        .finally(() => this.isLoadingActions = false);
    },
    getCampaigns() {
      if (this.isLoadingCampaigns) {
        return Promise.reject(new NonCriticalError(this.tr('Please wait until the previous operation is completed.')));
      }

      this.isLoadingCampaigns = true;

      return ReportService.getCampaigns({
        tokenPart1: this.tokenPart1,
        tokenPart2: this.tokenPart2,
        adAccountId: this.selectedAdAccountId,
      })
        .then(resp => {
          resp.unshift(this.buildAllCampaignsOption());
          this.campaigns = resp;

          // Reset selected campaign.
          const campaignIds = this.campaigns.map(campaign => campaign.id);
          if (this.selectedCampaignId && !campaignIds.includes(this.selectedCampaignId)) {
            this.selectedCampaignId = null;
          }

          return resp;
        })
        .catch(err => this.onError(err))
        .finally(() => this.isLoadingCampaigns = false);
    },
    getTotals() {
      if (this.isLoadingTotals) {
        return Promise.reject(new NonCriticalError(this.tr('Please wait until the previous operation is completed.')));
      }

      this.isLoadingTotals = true;

      return ReportService.getTotals({
        tokenPart1: this.tokenPart1,
        tokenPart2: this.tokenPart2,
        adAccountId: this.selectedAdAccountId,
        campaignId: this.selectedCampaignId,
        period: this.selectedPeriod,
        action: this.selectedAction,
      })
        .then(resp => this.totals = resp)
        .catch(err => this.onError(err))
        .finally(() => this.isLoadingTotals = false);
    },
    fetchReportByToken() {
      if (this.isFetchingToken) {
        return Promise.reject(new NonCriticalError(this.tr('Please wait until the previous operation is completed.')));
      }

      this.isFetchingToken = true;

      return ReportService.getByToken(this.tokenPart1, this.tokenPart2)
        .then(resp => {
          this.sharedAdAccount = resp.ad_account;

          if (resp.campaign) {
            this.campaigns = [ resp.campaign ];
            this.selectedCampaignId = resp.campaign.id;
          }

          this.selectedAdAccountId = this.sharedAdAccount.id;
          this.selectedPeriod = resp.period;
          this.selectedAction = resp.action;

          return resp;
        })
        .catch(err => {
          this.onError(err);
          this.isInvalidReportToken = true;
        })
        .finally(() => this.isFetchingToken = false);
    },
    onError(err) {
      this.hasError = true;
      this.error(err);
    },
    share() {
      if (this.isLoadingShare) {
        return;
      }
      if (!this.completedFilters) {
        this.error(this.tr('Choose an advertising account!'));
        return;
      }

      this.isLoadingShare = true;

      ReportService.share({
        adAccountId: this.selectedAdAccountId,
        campaignId: this.selectedCampaignId,
        period: this.selectedPeriod,
        action: this.selectedAction,
      })
        .then(resp => {
          this.shareUrl = resp.url;
          this.$fullscreenModal.open(
            this,
            ReportShareModal,
            { shareUrl: this.shareUrl },
          );

          return resp;
        })
        .catch(err => this.onError(err))
        .finally(() => this.isLoadingShare = false);
    },
    getDefaultAction() {
      return ReportService.getDefaultAction({
        campaignId: this.selectedCampaignId,
      })
        .then(resp => {
          if (resp.action) {
            this.selectedAction = resp.action;
          }
          return resp;
        });
    },
  },
};
</script>
